<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200 h-full flex flex-col">
    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
      <div class="flex items-center space-x-4">
        <h2 class="text-xl font-semibold text-gray-900">
          RFID Tag Management
        </h2>
        <div class="flex items-center space-x-2 text-sm text-gray-500">
          <span>{{ filteredTags.length }} tags</span>
          <span v-if="selectedDeviceType">for {{ selectedDeviceType }}</span>
        </div>
      </div>
      
      <div class="flex items-center space-x-3">
        <!-- Device Type Filter -->
        <select 
          v-model="selectedDeviceType" 
          class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Device Types</option>
          <option value="ESP32_relationship_hub">ESP32 Relationship Hub</option>
        </select>
        
        <!-- Add Tag Button -->
        <button
          @click="showAddTagModal = true"
          class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
        >
          <span class="mr-2">+</span>
          Add Tag
        </button>
        
        <!-- Refresh Button -->
        <button
          @click="refreshTags"
          :disabled="rfidTagStore.isLoading"
          class="px-3 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 text-sm"
        >
          <span class="text-lg">↻</span>
        </button>
      </div>
    </div>

    <!-- Error Display -->
    <div v-if="rfidTagStore.error" class="mx-6 mt-4">
      <div class="bg-red-50 border border-red-200 rounded-md p-4">
        <div class="flex items-start">
          <svg class="h-5 w-5 text-red-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
          <div class="ml-3">
            <p class="text-sm text-red-700">{{ rfidTagStore.error }}</p>
            <button 
              @click="rfidTagStore.clearError" 
              class="mt-1 text-xs text-red-600 hover:text-red-800 underline"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="rfidTagStore.isLoading && filteredTags.length === 0" class="flex-1 flex items-center justify-center">
      <div class="text-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p class="text-gray-500">Loading RFID tags...</p>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredTags.length === 0" class="flex-1 flex items-center justify-center">
      <div class="text-center py-12">
        <svg class="h-16 w-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
        </svg>
        <h3 class="text-lg font-medium text-gray-900 mb-2">No RFID tags found</h3>
        <p class="text-gray-500 mb-6">
          {{ selectedDeviceType ? 
            `No tags found for ${selectedDeviceType}` : 
            'No RFID tags have been configured yet' }}
        </p>
        <button
          @click="showAddTagModal = true"
          class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Add Your First Tag
        </button>
      </div>
    </div>

    <!-- Tags List -->
    <div v-else class="flex-1 overflow-auto">
      <div class="px-6 py-4">
        <div class="grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          <div
            v-for="tag in filteredTags"
            :key="tag._id"
            class="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-gray-300 transition-colors"
          >
            <!-- Tag Header -->
            <div class="flex items-start justify-between mb-3">
              <div class="flex-1 min-w-0">
                <div class="flex items-center space-x-2 mb-1">
                  <h3 class="text-sm font-medium text-gray-900 truncate">
                    {{ tag.name }}
                  </h3>
                  <span 
                    :class="tag.metadata?.scanMethod === 'nfc_scan' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  >
                    {{ tag.metadata?.scanMethod === 'nfc_scan' ? '✔️ Scanned' : '✍️ Manual' }}
                  </span>
                </div>
                <p class="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded">
                  {{ tag.tagId }}
                </p>
              </div>
              
              <!-- Actions Menu -->
              <div class="relative ml-3">
                <button
                  @click="toggleTagMenu(tag._id)"
                  class="text-gray-400 hover:text-gray-600 p-1 rounded"
                >
                  <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path d="M10 4a2 2 0 100-4 2 2 0 000 4z" />
                    <path d="M10 20a2 2 0 100-4 2 2 0 000 4z" />
                  </svg>
                </button>
                
                <!-- Dropdown Menu -->
                <div 
                  v-if="openMenuId === tag._id"
                  class="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 min-w-32"
                >
                  <button
                    @click="editTag(tag)"
                    class="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Edit
                  </button>
                  <button
                    @click="markTagAsUsed(tag)"
                    class="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Mark as Used
                  </button>
                  <button
                    @click="deleteTag(tag)"
                    class="block w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>

            <!-- Tag Details -->
            <div class="space-y-2 text-xs text-gray-600">
              <div class="flex items-center space-x-2">
                <span class="font-medium">Type:</span>
                <span class="bg-blue-100 text-blue-800 px-2 py-0.5 rounded">{{ tag.deviceType }}</span>
                <!-- E-Ink Image Indicator -->
                <span 
                  v-if="tag.deviceType === 'ESP32_relationship_hub' && tag.metadata?.eInkImage"
                  class="bg-purple-100 text-purple-800 px-2 py-0.5 rounded flex items-center space-x-1"
                  title="Custom e-ink display image configured"
                >
                  <svg class="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
                  </svg>
                  <span>Display Image</span>
                </span>
              </div>
              
              <div v-if="tag.metadata?.description" class="flex items-start space-x-2">
                <span class="font-medium">Note:</span>
                <span class="flex-1">{{ tag.metadata.description }}</span>
              </div>
              
              <div class="flex items-center space-x-4 pt-2 border-t border-gray-200">
                <span>Created: {{ formatDate(tag.createdAt) }}</span>
                <span v-if="tag.lastUsed">•</span>
                <span v-if="tag.lastUsed" class="text-green-600">
                  Used: {{ formatDate(tag.lastUsed) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Tag Modal -->
    <RFIDTagModal
      v-if="showAddTagModal || editingTag"
      :tag="editingTag"
      :is-edit="!!editingTag"
      @save="handleSaveTag"
      @cancel="handleCancelModal"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRFIDTagStore } from '../stores/rfidTagStore.js'
import RFIDTagModal from './RFIDTagModal.vue'

// Store
const rfidTagStore = useRFIDTagStore()

// Local state
const selectedDeviceType = ref('')
const showAddTagModal = ref(false)
const editingTag = ref(null)
const openMenuId = ref(null)

// Computed
const filteredTags = computed(() => {
  if (!selectedDeviceType.value) {
    return rfidTagStore.tagList
  }
  return rfidTagStore.getTagsByDeviceType(selectedDeviceType.value)
})

// Methods
const refreshTags = async () => {
  await rfidTagStore.fetchTags(selectedDeviceType.value, true)
}

const editTag = (tag) => {
  editingTag.value = { ...tag }
  openMenuId.value = null
}

const deleteTag = async (tag) => {
  if (confirm(`Are you sure you want to delete the tag "${tag.name}"?`)) {
    try {
      await rfidTagStore.deleteTag(tag._id)
    } catch (error) {
      console.error('Failed to delete tag:', error)
    }
  }
  openMenuId.value = null
}

const markTagAsUsed = async (tag) => {
  try {
    await rfidTagStore.markTagAsUsed(tag._id)
  } catch (error) {
    console.error('Failed to mark tag as used:', error)
  }
  openMenuId.value = null
}

const handleSaveTag = async (tagData) => {
  try {
    if (editingTag.value) {
      // Update existing tag
      await rfidTagStore.updateTag(editingTag.value._id, tagData)
    } else {
      // Create new tag
      await rfidTagStore.createTag(tagData)
    }
    
    handleCancelModal()
  } catch (error) {
    console.error('Failed to save tag:', error)
    // Error is already set in store
  }
}

const handleCancelModal = () => {
  showAddTagModal.value = false
  editingTag.value = null
}

const toggleTagMenu = (tagId) => {
  openMenuId.value = openMenuId.value === tagId ? null : tagId
}

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Click outside handler for menu
const handleClickOutside = (event) => {
  if (!event.target.closest('.relative')) {
    openMenuId.value = null
  }
}

// Lifecycle
onMounted(async () => {
  // Load initial data
  await refreshTags()
  
  // Add click outside listener
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* Additional component styles if needed */
</style>